import { Line } from 'react-chartjs-2'
import { useColorMode } from '@/components/ui/color-mode'

// Modern chart palette: saturated mid-range colours readable on both light and dark backgrounds
const CHART_COLOURS = [
  '#6366f1', // indigo
  '#0ea5e9', // sky
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6' // teal
]
export { CHART_COLOURS }

// Returns Chart.js options with grid/tick/legend colours set for the current colour mode.
// Deep-merges with any caller-provided options so neither overrides the other unexpectedly.
export function useChartTheme (userOptions = {}) {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'

  // Match our neutral token values: neutral.400 in dark, neutral.500 in light
  const textColor = isDark ? '#9CA3AF' : '#6B7280'
  // Subtle grid lines that recede behind the data
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  const themeDefaults = {
    plugins: {
      legend: {
        labels: { color: textColor }
      }
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: textColor }
      },
      y: {
        grid: { color: gridColor },
        ticks: { color: textColor }
      }
    }
  }

  // Merge: theme defaults first, then user options layered on top at each level
  return {
    ...themeDefaults,
    ...userOptions,
    plugins: {
      ...themeDefaults.plugins,
      ...userOptions.plugins,
      legend: {
        ...themeDefaults.plugins.legend,
        ...userOptions.plugins?.legend,
        labels: {
          ...themeDefaults.plugins.legend.labels,
          ...userOptions.plugins?.legend?.labels
        }
      }
    },
    scales: {
      ...themeDefaults.scales,
      ...userOptions.scales,
      x: {
        ...themeDefaults.scales.x,
        ...userOptions.scales?.x,
        grid: { ...themeDefaults.scales.x.grid, ...userOptions.scales?.x?.grid },
        ticks: { ...themeDefaults.scales.x.ticks, ...userOptions.scales?.x?.ticks }
      },
      y: {
        ...themeDefaults.scales.y,
        ...userOptions.scales?.y,
        grid: { ...themeDefaults.scales.y.grid, ...userOptions.scales?.y?.grid },
        ticks: { ...themeDefaults.scales.y.ticks, ...userOptions.scales?.y?.ticks }
      }
    }
  }
}

const DEFAULT_POINT_RADIUS = 2

export default function SimpleLineChart ({ labels, datasets, options }) {
  const mergedOptions = useChartTheme(options)

  datasets.forEach((dataset, i) => {
    const colour = CHART_COLOURS[i % CHART_COLOURS.length]
    dataset.backgroundColor = colour
    dataset.borderColor = dataset.borderColor ?? colour
    dataset.borderWidth = dataset.borderWidth ?? 2
    if (dataset.pointRadius === undefined) {
      dataset.pointRadius = DEFAULT_POINT_RADIUS
    }
  })

  return (
    <Line
      options={mergedOptions}
      data={{ labels, datasets }}
    />
  )
}
